import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ITreeOptions } from 'angular-tree-component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { MapApi } from '../../../api/map-api';
import { UserApi } from '../../../api/user-api';
import { IMCMapView } from '../../../classes/mcMapView';
import { environment } from '../../../environment';
import { BooleanConverter, InputConverter } from '../../../inputConverter';
import { IAuthenticationService } from '../../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { MapService } from '../../../services/map.service';
import { IMapCoreMap, IUserFolder, IUserMap, LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';

/* tslint:disable:template-cyclomatic-complexity */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-map-catalog',
    styleUrls: [
        '../../sharedStyles/base.scss',
        '../../sharedStyles/telerik.scss',
        '../../sharedStyles/angular-tree-component.css',
        './map-catalog.component.scss'
    ],
    templateUrl: './map-catalog.component.html'
})
export class MapCatalogComponent extends BaseComponent implements OnInit, AfterViewInit {

    @Input() applicationId: number = environment.appId;
    @Input() mergeMaps = false;
    @Input() @InputConverter(BooleanConverter) showOnlyApplicationUserMaps: boolean = false;
    @Input() mapViewRef: IMCMapView;
    @Input() tabIndex: number = 0;
    @Input() loggedIn: boolean = false;

    private _userId = null;
    @Input()
    get userId() { return this._userId; }
    set userId(value: number) {
        this._userId = value;
        this.loadUserData();
        this.cdr.detectChanges();
    }

    private _showSystemMaps: boolean;
    @Input()
    @InputConverter(BooleanConverter)
    get showSystemMaps() {
        return this._showSystemMaps;
    }
    set showSystemMaps(value: boolean) {
        this._showSystemMaps = value;
        this.cdr.detectChanges();
    }

    private _showUserMaps: boolean;
    @Input()
    @InputConverter(BooleanConverter)
    get showUserMaps() {
        return this._showUserMaps;
    }
    set showUserMaps(value: boolean) {
        this._showUserMaps = value;
        this.cdr.detectChanges();
    }

    _active: boolean;
    @Input()
    get active() {
        return this._active;
    }
    set active(value: boolean) {
        value = BooleanConverter(value);
        if (value) {
            this.onActivate();
        }
        this._active = value;
    }

    @Output() readonly shouldLogIn: EventEmitter<MouseEvent> = new EventEmitter();
    @Output() readonly ondone: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() readonly onmapopened: EventEmitter<any> = new EventEmitter<any>();
    @Output() readonly openmergemap: EventEmitter<any> = new EventEmitter<any>();
    @Output() readonly opensharemap: EventEmitter<any[]> = new EventEmitter<any[]>();

    systemMaps: IMapCoreMap[] = [];
    limit: number = 10;
    current: number;
    searchInputForm: FormGroup;
    searchInputControl: FormControl = new FormControl();
    debounceTime = 500;
    searchTerm: any;
    searchText: string = '';
    filteredResultCount = { Count: 0 };
    systemMapErrorMessage = null;
    userMaps: IUserMap[] = [];
    filteredUserMaps: IUserMap[] = [];
    userMapFolders: any[] = [];
    userMapErrorMessage = null;
    multiSelectEnabled: boolean = false;
    canViewUsers = false;

    loading: { status: boolean; text: string };
    systemLoading: { status: boolean; text: string } = { status: false, text: 'Loading...' };
    showFolderEditor: boolean;
    showFolderPicker: boolean;
    folderEditing = false;
    newFolderName: FormControl;
    parentFolders: IUserFolder[] = [];
    currentFolderId: number;
    parentFolderId: number;
    currentMapId: number;
    userMapSortByEdited: boolean = false;

    get pageSize(): number {
        return Math.ceil(this.filteredResultCount.Count / this.limit);
    }

    get pageSizeArray(): [] {
        return [].constructor(this.pageSize);
    }

    userMapNodes: any[];
    treeOptions: ITreeOptions;
    @ViewChild('userMapTree') userMapTree;

    constructor(
        _vcr: ViewContainerRef,
        private readonly _formBuilder: FormBuilder,
        private readonly mapService: MapService,
        private readonly userApi: UserApi,
        private readonly mapApi: MapApi,
        @Inject('authenticationService') private readonly authService: IAuthenticationService,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(_vcr);
        this.registerEvent('shouldLogIn', this.shouldLogIn);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onmapopened', this.onmapopened);
        this.registerEvent('openmergemap', this.openmergemap);
        this.registerEvent('opensharemap', this.opensharemap);

        this.searchInputForm = this._formBuilder.group({
            searchInput: this.searchInputControl
        });
        authService.permissions.subscribe(permissions => {
            this.canViewUsers = permissions && permissions.includes('View Users');
        });
    }

    ngOnInit() {
        this.searchInputControl
            .valueChanges
            .pipe(
                debounceTime(this.debounceTime),
                distinctUntilChanged()
            ).subscribe((searchText: string) => {
                this.searchText = searchText;
                this.searchTerm = { keys: ['name', 'description'], searchTerm: searchText };
                this.current = 0;
                this.loadFilteredUserMaps();
            });

        this.treeOptions = {};

        this.loading = { status: false, text: 'Loading...' };
        this.newFolderName = new FormControl('', [Validators.required, this.nameInUse.bind(this)]); //tslint:disable-line
    }

    get mapsSelected() {
        if (this.userMaps) {
            return this.userMaps.map(x => x.map).filter(x => {if (x && x.selected) {return x; }});
        }
        return [];
    }

    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        this.isReady();
    }

    async onActivate() {
        this.clearError();
        const loadingPromises = [];
        this.multiSelectEnabled = false;

        if (this._showSystemMaps) {
            loadingPromises.push(this.loadSystemMaps());
        }

        if (this.userId === null) {
            this.authService.GetCurrentUser().subscribe(user => {
                if (user) {
                    this.userId = user.id;
                }
            });
        } else {
            loadingPromises.push(this.loadUserData());
        }

        //await each task while they work in the background so no hold up of user maps is done by awaiting system maps
        await Promise.all(loadingPromises);
    }

    async loadUserData() {
        this.loading = { status: true, text: 'Loading...' };
        if (this.userId) {
            this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, 'maps');
            this.parentFolders.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            this.parentFolders.push({ name: 'No parent', id: 0 });
        }
        await this.getUserMapsAndFoldersForUserId();
        this.loadFilteredUserMaps();
        this.loading.status = false;
        this.cdr.detectChanges();
    }

    async loadSystemMaps() {
        this.systemLoading = { status: true, text: 'Loading...' };
        this.systemMaps = [];
        try {
            const applications = await this.mapService.getSystemMaps(this.applicationId, false);
            const maps = applications[0].maps;
            for (const map of maps) {
                this.systemMaps.push(map);
            }
            this.systemMaps.sort((a, b) => a.name.localeCompare(b.name));
            this.current = 0;
            this.filteredResultCount.Count = this.systemMaps.length;
            this.systemLoading.status = false;
            this.cdr.detectChanges();
        } catch (error) {
            this.systemMapErrorMessage = 'There was an error fetching maps';
            this.LoggingService.Log('Error fetching maps from database', error, LogLevel.Error);
        }
    }

    async getUserMapsAndFoldersForUserId() {
        this.userMaps = [];
        if (this.userId) {
            //start a task to get the user folders in the background
            //wrap the whole method in a try to avoid needing two trys for both getting folders and maps
            try {
                const getUserMapFoldersPromise = this.mapService.getUserMapsFolders(this.userId, this.applicationId);
                //start getting the user maps

                let appIdFilter = null;
                if (this.showOnlyApplicationUserMaps) {
                    appIdFilter = this.applicationId;
                }
                //If appId is not null, then only user maps with associated with this application are fetched.
                const userMaps = await this.mapService.getUserMaps(this.userId, appIdFilter);
                //get the maps by the provided ids back from UserMaps to get their data
                this.userMaps = userMaps && userMaps[0] && userMaps[0].userMaps ? userMaps[0].userMaps : [];
                // Set date strings to dates
                this.userMaps.filter(x => x.map).forEach(userMap => {
                    if (typeof userMap.map.lastEditedDate === 'string') {
                        userMap.map.lastEditedDate = new Date(userMap.map.lastEditedDate);
                    }
                    if (typeof userMap.map.createdDate === 'string') {
                        userMap.map.createdDate = new Date(userMap.map.createdDate);
                    }
                });
                const userMapFolders = await getUserMapFoldersPromise;
                this.userMapFolders = userMapFolders && userMapFolders[0] && userMapFolders[0].folders ? userMapFolders[0].folders : [];
            } catch (error) {
                this.systemMapErrorMessage = 'There was an error fetching maps';
                this.LoggingService.Log('Error fetching maps from database', error, LogLevel.Error);
            }
        } else {
            this.userMaps = [];
            this.userMapFolders = [];
        }
    }
    loadFilteredUserMaps() {
        this.filteredUserMaps = this.userMaps.filter(x => {if (x.map) {return this.isMatchToSearchText(x.map.name, x.map.description); }});
        this.setTree(this.userMapFolders, this.filteredUserMaps);
        this.treeOptions = {};
    }

    isMatchToSearchText(name: string, description?: string) {
        const nameLower = name.toLowerCase();
        const searchTextLower = this.searchText.toLowerCase();
        const searchTermArray = searchTextLower.split(' ');
        let allTermsFoundName = true;
        for (const term of searchTermArray) {
            allTermsFoundName = allTermsFoundName && nameLower.indexOf(term) >= 0;
        }
        let allTermsFoundDesc = false;
        if (description) {
            const descLower = description.toLowerCase();
            allTermsFoundDesc = true;
            for (const term of searchTermArray) {
                allTermsFoundDesc = allTermsFoundDesc && descLower.indexOf(term) >= 0;
            }
        }
        return allTermsFoundName || allTermsFoundDesc;
    }

    clearError() {
        this.systemMapErrorMessage = null;
        this.userMapErrorMessage = null;
    }

    setTree(userMapsFolders, userMaps: IUserMap[]) {
        this.userMapNodes = [];
        const userMapNodesTemp = [];
        let userMapSort = (a: IUserMap, b: IUserMap) => a.map.name.localeCompare(b.map.name);
        if (this.userMapSortByEdited) {
            userMapSort = (a: IUserMap, b: IUserMap) => b.map.lastEditedDate.valueOf() - a.map.lastEditedDate.valueOf();
        }
        if (userMapsFolders) {
            userMapsFolders.sort((a, b) => a.name.localeCompare(b.name));
            userMaps.sort(userMapSort);

            userMapNodesTemp.push(...userMapsFolders.filter(f => !f.parentUserFolderId || f.parentUserFolderId === 0).map(f => this.buildTreeNode(f, userMapsFolders, userMaps)));
        }
        // Add orphan maps
        userMapNodesTemp.push(...userMaps.map(m => {
            const mapObj = m.map;
            mapObj.accessLevel = m.accessLevel;
            mapObj.sharedWith = m.sharedWith || [];
            return {
                children: [],
                id: m.map.id,
                map: mapObj,
                name: m.map.name,
                nodeType: 'leaf',
                owner: this.isUserOwnerOfMap(m)
            };
        }));
        this.userMapNodes = userMapNodesTemp;
    }

    getChildMaps(folder: any, userMaps: IUserMap[]) {
        const childMaps = [];
        folder.maps.forEach(fm => {
            const userMap = userMaps.find(um => um.map.id === fm.id);
            if (userMap) {
                childMaps.push(userMap);
                const idx = userMaps.indexOf(userMap);
                userMaps.splice(idx, 1);

            }
        });
        let userMapSort = (a: IUserMap, b: IUserMap) => a.map.name.localeCompare(b.map.name);
        if (this.userMapSortByEdited) {
            userMapSort = (a: IUserMap, b: IUserMap) => b.map.lastEditedDate.valueOf() - a.map.lastEditedDate.valueOf();
        }
        childMaps.sort(userMapSort);
        return childMaps;
    }

    buildTreeNode(folder: any, userfolders: any[], userMaps: IUserMap[]) {
        const childFolders = userfolders.filter(f => f.parentUserFolderId === folder.id);
        const childMaps = this.getChildMaps(folder, userMaps);
        const groupNode = {
            children: [],
            id: folder.id,
            name: folder.name,
            nodeType: 'group'
        };
        groupNode.children.push(...childMaps.map(m => {
            m.map.accessLevel = m.accessLevel;
            m.map.sharedWith = m.sharedWith;
            return {
                children: [],
                id: m.map.id,
                map: m.map,
                name: m.map.name,
                nodeType: 'leaf',
                owner: this.isUserOwnerOfMap(m)
            };
        }));
        groupNode.children.push(...childFolders.map(cf => this.buildTreeNode(cf, userfolders, userMaps)));
        return groupNode;
    }

    go(page: number) {
        this.current = page;
    }

    previous() {
        if (this.current === 0) {
            return;
        }
        this.current--;
    }

    next() {
        if (this.current === this.pageSize - 1) {
            return;
        }
        this.current++;
    }

    editFolder(folder) {
        this.currentFolderId = folder.id;
        const parentFolder = this.parentFolders.find(f => f.id === folder.id);
        this.parentFolderId = parentFolder ? parentFolder.parentUserFolderId : null;
        this.newFolderName.setValue(folder.name);
        this.showFolderEditor = true;
        this.folderEditing = true;
    }

    addFolder(folder) {
        this.currentFolderId = null;
        this.parentFolderId = folder ? folder.id : undefined;
        this.showFolderEditor = true;
        this.newFolderName.setValue('');
        this.folderEditing = false;
    }

    async newFolder() {
        const folder: any = {
            name: this.newFolderName.value,
            category: 'maps',
            parentUserFolderId: this.parentFolderId,
            userId: this.userId,
            applicationId: this.applicationId
        };

        if (this.currentFolderId) {
            folder.id = this.currentFolderId;
        }

        try {
            this.showFolderEditor = false;
            this.loading = { status: true, text: 'Creating new folder...' };
            await this.userApi.createNewUserFolder(folder);

            await this.getUserMapsAndFoldersForUserId();
            this.loadFilteredUserMaps();
            this.parentFolders = await this.userApi.queryUserFolders(this.userId, this.applicationId, 'maps');
            this.parentFolders.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
            this.parentFolders.push({ name: 'No parent', id: 0 });
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            this.loading.status = false;
        }
    }

    async deleteFolder(folder) {
        if (window.confirm('Are you sure you want to delete this folder and all maps it contains?')) {
            try {
                this.loading = { status: true, text: 'Deleting folder...' };
                await this.deleteFolderAndMaps(folder);
            } catch (error) {
                this.systemMapErrorMessage = 'There was an error deleting the folder.';
                this.LoggingService.Log('Error deleting folder.', error, LogLevel.Error);
            } finally {
                this.loading = { status: true, text: 'Loading...' };
                this.parentFolders = this.parentFolders.filter(f => f.id !== folder.id);
                await this.getUserMapsAndFoldersForUserId();
                this.loadFilteredUserMaps();
                this.loading.status = false;
            }
        }
    }

    async deleteFolderAndMaps(folder) {
        for (const child of folder.children) {
            if (child.nodeType === 'group') {
                await this.deleteFolderAndMaps(child);
            } else {
                await this.mapApi.saveMapToUserFolder(child.id, 0, folder.id);
                await this.deleteMap(child.map, false);
                if (child.id === this.mapViewRef.mapcoreViewProps.id) {
                    this.resetMap();
                }
            }
        }

        await this.userApi.deleteUserFolder(folder.id);
    }

    pickFolder(mapId) {
        this.currentMapId = mapId;
        const parentFolder = this.userMapFolders.find(f => f.maps.find(m => m.id === mapId));
        if (parentFolder) {
            this.parentFolderId = parentFolder.id;
        }
        this.showFolderPicker = true;
    }

    async changeFolder() {
        this.showFolderPicker = false;
        this.loading = { status: true, text: 'Changing folder...' };
        try {
            const currentFolder = this.userMapFolders.find(umf => umf.maps && umf.maps.length && umf.maps.some(m => m.id === this.currentMapId));
            await this.mapApi.saveMapToUserFolder(this.currentMapId, this.parentFolderId, !!currentFolder ? currentFolder.id : null);
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            await this.getUserMapsAndFoldersForUserId();
            this.loadFilteredUserMaps();
            this.loading.status = false;
        }
    }

    async deleteMap(map, isSingle = true) {
        try {
            const isOwner = this.isUserOwnerOfMap(map);

            if (isOwner) {
                if (isSingle) {
                    if (window.confirm('Are you sure you want to delete this map?')) {
                        this.loading = { status: true, text: 'Deleting Map...' };
                        await this.mapApi.deleteMap(map.id);
                    }
                } else {
                    this.loading = { status: true, text: 'Deleting Maps...' };
                    await this.mapApi.deleteMap(map.id);
                }
            } else {
                if (isSingle) {
                    if (window.confirm('Are you sure you want to no longer have this map shared with you?')) {
                        this.loading = { status: true, text: 'Removing Shared Map Association...' };
                        /* remove shared map association */
                        await this.mapApi.unShareMap([this.userId], map.id);
                    }
                } else {
                    this.loading = { status: true, text: 'Removing Shared Map Associations...' };
                    /* remove shared map association */
                    await this.mapApi.unShareMap([this.userId], map.id);
                }

            }

            if (map.id === this.mapViewRef.mapcoreViewProps.id) {
                this.resetMap();
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            if (isSingle) {
                await this.getUserMapsAndFoldersForUserId();
                this.loadFilteredUserMaps();
                this.loading.status = false;
            }
            this.cdr.detectChanges();
        }
    }

    async deleteMulti() {
        if (window.confirm('Are you sure you want to delete these maps?')) {
            const mapsToDelete = this.mapsSelected;
            if (mapsToDelete.length > 0) {
                this.loading = { status: true, text: 'Deleting Maps...' };
                const deleteMapIds: number[] = mapsToDelete.filter(m => this.isUserOwnerOfMap(m)).map(m => m.id);
                const unshareMapIds: number[] = mapsToDelete.filter(m => !this.isUserOwnerOfMap(m)).map(m => m.id);

                try {
                    await this.mapApi.deleteMaps(deleteMapIds);
                    await this.mapApi.unShareMaps([this.userId], unshareMapIds);
                }
                catch (error) {
                    this.systemMapErrorMessage = 'There was an error deleting the maps.';
                    this.LoggingService.Log('Error deleting maps.', error, LogLevel.Error);
                }
                finally {
                    if (mapsToDelete.filter(m => m.id === this.mapViewRef.mapcoreViewProps.id)) {
                        this.resetMap();
                    }

                    await this.getUserMapsAndFoldersForUserId();
                    this.loadFilteredUserMaps();

                    this.multiSelectEnabled = false;
                    mapsToDelete.forEach(x => x.selected = false);
                    this.loading.status = false;
                }
            }
        }
    }

    async shareMulti() {
        const mapsToShare = this.mapsSelected;
        if (this.canViewUsers) {
            if (mapsToShare.length > 0) {
                this.opensharemap.emit(mapsToShare);
                await this.closeWindow();
            }
        } else if (mapsToShare.every(x => x.isPublic)) {
            let mailto = 'mailto:?';
            mailto += `subject=${environment.appName}: New Shared Maps&`;
            for (const map of mapsToShare) {
                mailto += `body=I've shared a new map with you from ${environment.appName} named ${map.name}. You can view the map here: ${this.mapService.getMapUrl(map.id)}`;
            }
            window.location.href = mailto;
        }

    }

    async unshareMulti() {
        if (window.confirm('Are you sure that you would like to remove all shared users from these maps?')) {
            const mapsToUnshare = this.mapsSelected;
            try {
                this.loading = { status: true, text: 'Unsharing Maps...' };
                for (const map of mapsToUnshare) {
                    const userIds = map.sharedWith.map(x => x.userId);
                    await this.mapService.unShareMap(userIds, map.id);
                }
            } catch (e) {
                this.systemMapErrorMessage = 'There was an error unsharing the maps.';
                this.LoggingService.Log('Error unsharing maps.', e, LogLevel.Error);
            } finally {
                await this.getUserMapsAndFoldersForUserId();
                this.loadFilteredUserMaps();

                this.multiSelectEnabled = false;
                mapsToUnshare.forEach(x => x.selected = false);
                this.loading.status = false;
            }
        }
    }

    resetMap() {
        this.mapViewRef.mapcoreViewProps.id = null;
        if (this.mapViewRef.mapcoreViewProps.layers) {
            this.mapViewRef.mapcoreViewProps.layers.forEach(layer => {
                layer.id = null;
            });
        }
    }

    closeWindow() {
        this.userMapNodes = null;
        this.ondone.emit(true);
    }

    mapOpened(e) {
        this.onmapopened.emit(e);
    }

    mapMerged(e) {
        this.mergeMaps = false;
        this.openmergemap.emit(e);
        this.closeWindow();
    }

    nameInUse(control: AbstractControl) {
        const parentFolder = this.parentFolders.find(f => f.id === this.currentFolderId);
        if (this.currentFolderId && (!parentFolder || control.value === parentFolder.name)) {
            return null;
        }

        return this.parentFolders
            .map(f => f.name)
            .includes(control.value) ? { nameInUse: { value: control.value } } : null;
    }

    logIn(event: MouseEvent) {
        this.shouldLogIn.emit(event);
    }

    toggleMultiSelect() {
        this.multiSelectEnabled = !this.multiSelectEnabled;
    }

    isUserOwnerOfMap(map): boolean {
        return map.accessLevel === 'OWNER';
    }
}
